// Vistas del módulo POS (panel administrativo).
//
// El cobro NO vive aquí: eso es la app del mostrador. Este módulo es la
// ventana del administrador — qué se vendió, en qué punto, qué se corrigió
// y cómo cerró la caja.

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  ClipboardList,
  Loader2,
  Lock,
  MapPin,
  Store,
  Tag,
} from 'lucide-react';

import { posApi, PosApiError } from './api';
import {
  CancelSaleControl,
  CloseDayForm,
  Collapsible,
  LocationActiveToggle,
  NewItemForm,
  NewLocationForm,
  PriceEditor,
} from './forms';
import type { Closing, Location, SaleDay, SalesItem } from './types';

const money = (v: string | null | undefined): string =>
  v == null ? '—' : `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const hour = (iso: string): string =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

const STATUS_LABEL: Record<string, string> = {
  active: 'Activa',
  cancelled: 'Cancelada',
  amended: 'Corregida',
};

// ── Piezas compartidas ──────────────────────────────────────────

function Shell({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: typeof Store;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground leading-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function ErrorBox({ error }: { error: string }) {
  return (
    <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-500 flex items-start gap-2">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{error}</span>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-20 text-muted-foreground">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );
}

function Empty({ icon: Icon, title, hint }: { icon: typeof Store; title: string; hint: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Icon className="w-10 h-10 text-muted-foreground mb-3" />
      <p className="text-foreground font-medium">{title}</p>
      <p className="text-sm text-muted-foreground mt-1 max-w-md">{hint}</p>
    </div>
  );
}

/** Selector de punto de venta. Sin puntos dados de alta no hay nada que ver,
 *  así que lo dice en vez de mostrar una lista vacía sin explicación. */
function useLocations(includeInactive = false) {
  const [locations, setLocations] = useState<Location[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    posApi
      .locations(includeInactive)
      .then((rows) => alive && setLocations(rows))
      .catch((e: PosApiError) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [tick, includeInactive]);

  return { locations, error, reload: () => setTick((t) => t + 1) };
}

function LocationPicker({
  locations,
  value,
  onChange,
}: {
  locations: Location[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 mb-5">
      {locations.map((loc) => (
        <button
          key={loc.id}
          type="button"
          onClick={() => onChange(loc.id)}
          className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
            loc.id === value
              ? 'border-primary bg-primary/15 text-foreground font-medium'
              : 'border-border bg-card text-muted-foreground hover:text-foreground'
          }`}
        >
          <span
            className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
            style={{ backgroundColor: loc.color || 'currentColor' }}
          />
          {loc.name}
        </button>
      ))}
    </div>
  );
}

// ── Ventas del día ──────────────────────────────────────────────

export function PosVentasView() {
  const { locations, error: locError } = useLocations();
  const [locationId, setLocationId] = useState('');
  const [day, setDay] = useState<SaleDay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const puntos = (locations || []).filter((l) => l.kind === 'point_of_sale');

  useEffect(() => {
    if (!locationId && puntos.length) setLocationId(puntos[0].id);
  }, [puntos, locationId]);

  const load = useCallback(() => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    posApi
      .day(locationId)
      .then(setDay)
      .catch((e: PosApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [locationId]);

  useEffect(load, [load]);

  if (locError) return <Shell icon={Store} title="Ventas del día" subtitle="Cobros registrados por punto"><ErrorBox error={locError} /></Shell>;
  if (!locations) return <Shell icon={Store} title="Ventas del día" subtitle="Cobros registrados por punto"><Spinner /></Shell>;

  if (!puntos.length) {
    return (
      <Shell icon={Store} title="Ventas del día" subtitle="Cobros registrados por punto">
        <Empty
          icon={MapPin}
          title="No hay puntos de venta dados de alta"
          hint="Da de alta al menos un punto en la pestaña Puntos. Sin punto no hay dónde registrar un cobro."
        />
      </Shell>
    );
  }

  return (
    <Shell icon={Store} title="Ventas del día" subtitle="Cobros registrados por punto">
      <LocationPicker locations={puntos} value={locationId} onChange={setLocationId} />
      {error && <ErrorBox error={error} />}
      {loading && <Spinner />}

      {day && !loading && (
        <>
          <div className="grid gap-3 sm:grid-cols-3 mb-5">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Cobros activos</p>
              <p className="text-2xl font-bold text-foreground tabular-nums">{day.count_active}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total del día</p>
              {day.total_active == null ? (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-2">
                  <Lock className="w-3.5 h-3.5" />
                  Requiere permiso <code className="text-xs">pos:totals</code>
                </p>
              ) : (
                <p className="text-2xl font-bold text-foreground tabular-nums">{money(day.total_active)}</p>
              )}
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Estado</p>
              <p className="text-lg font-semibold text-foreground mt-1">
                {day.day_closed ? 'Cerrado' : 'Abierto'}
              </p>
            </div>
          </div>

          {!day.day_closed && (
            <div className="mb-5">
              <Collapsible label="Cerrar el día">
                {(close) => (
                  <CloseDayForm
                    locationId={locationId}
                    businessDate={day.business_date}
                    onDone={() => {
                      close();
                      load();
                    }}
                  />
                )}
              </Collapsible>
            </div>
          )}

          {!day.sales.length ? (
            <Empty
              icon={ClipboardList}
              title="Sin cobros hoy en este punto"
              hint="Los cobros aparecen aquí en cuanto el mostrador los sincroniza."
            />
          ) : (
            <div className="grid gap-2">
              {day.sales.map((s) => (
                <div
                  key={s.id}
                  className={`rounded-xl border border-border bg-card p-4 ${
                    s.status !== 'active' ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p
                        className={`font-semibold text-foreground tabular-nums ${
                          s.status === 'cancelled' ? 'line-through' : ''
                        }`}
                      >
                        {money(s.total)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {hour(s.sold_at)} · {s.cashier}
                      </p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full border border-border text-muted-foreground">
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {s.lines.map((l) => `${Number(l.quantity)}× ${l.item_name}`).join(' · ')}
                  </p>
                  {/* Solo se corrige el día en curso y solo si no está cerrado.
                      El candado real es del backend (409); esto evita ofrecer
                      un botón que va a fallar. */}
                  {s.status === 'active' && !day.day_closed && (
                    <CancelSaleControl saleId={s.id} onDone={load} />
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Shell>
  );
}

// ── Cierres ─────────────────────────────────────────────────────

export function PosCierresView() {
  const [closings, setClosings] = useState<Closing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { locations } = useLocations();

  useEffect(() => {
    posApi
      .closings()
      .then(setClosings)
      .catch((e: PosApiError) => setError(e.message));
  }, []);

  const nameOf = (id: string) => locations?.find((l) => l.id === id)?.name || '—';

  return (
    <Shell icon={ClipboardList} title="Cierres y cortes" subtitle="Lo vendido contra lo contado">
      {error && <ErrorBox error={error} />}
      {!closings && !error && <Spinner />}
      {closings && !closings.length && (
        <Empty
          icon={ClipboardList}
          title="Todavía no hay cierres"
          hint="Un cierre se crea desde el mostrador al terminar la jornada. Al registrarse, ese día queda bloqueado."
        />
      )}
      {closings && closings.length > 0 && (
        <div className="grid gap-2">
          {closings.map((c) => {
            const dif = Number(c.difference);
            return (
              <div key={c.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-semibold text-foreground">
                      {c.business_date} · {nameOf(c.location_id)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Cerró {c.closed_by}
                      {c.received_by ? ` · recibió ${c.received_by}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-bold tabular-nums ${
                        dif === 0 ? 'text-foreground' : dif < 0 ? 'text-red-500' : 'text-amber-500'
                      }`}
                    >
                      {dif > 0 ? '+' : ''}
                      {money(c.difference)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      esperado {money(c.expected_total)} · contado {money(c.counted_cash)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Shell>
  );
}

// ── Catálogo ────────────────────────────────────────────────────

export function PosCatalogoView() {
  const [items, setItems] = useState<SalesItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = () => setTick((t) => t + 1);

  useEffect(() => {
    posApi
      .items()
      .then(setItems)
      .catch((e: PosApiError) => setError(e.message));
  }, [tick]);

  return (
    <Shell icon={Tag} title="Catálogo" subtitle="Artículos cobrables y su precio vigente">
      <div className="mb-4">
        <Collapsible label="Nuevo artículo">
          {(close) => (
            <NewItemForm
              onDone={() => {
                close();
                reload();
              }}
            />
          )}
        </Collapsible>
      </div>
      {error && <ErrorBox error={error} />}
      {!items && !error && <Spinner />}
      {items && !items.length && (
        <Empty
          icon={Tag}
          title="Sin artículos en el catálogo"
          hint="Cada artículo necesita un precio vigente para poder cobrarse. Cambiar un precio no altera lo ya vendido: se guarda el historial."
        />
      )}
      {items && items.length > 0 && (
        <div className="grid gap-2">
          {items.map((it) => (
            <div
              key={it.id}
              className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3"
            >
              <div>
                <p className="font-medium text-foreground">{it.name}</p>
                <p className="text-xs text-muted-foreground">{it.slug}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                {it.current_price == null ? (
                  <span className="text-xs px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-500">
                    sin precio — no se puede cobrar
                  </span>
                ) : (
                  <span className="font-semibold text-foreground tabular-nums">
                    {money(it.current_price)}
                  </span>
                )}
                <PriceEditor itemId={it.id} onDone={reload} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

// ── Puntos ──────────────────────────────────────────────────────

export function PosPuntosView() {
  const { locations, error, reload } = useLocations(true);

  return (
    <Shell icon={MapPin} title="Puntos" subtitle="Dónde opera el negocio">
      <div className="mb-4">
        <Collapsible label="Nuevo punto">
          {(close) => (
            <NewLocationForm
              onDone={() => {
                close();
                reload();
              }}
            />
          )}
        </Collapsible>
      </div>
      {error && <ErrorBox error={error} />}
      {!locations && !error && <Spinner />}
      {locations && !locations.length && (
        <Empty
          icon={MapPin}
          title="Sin puntos dados de alta"
          hint="Un punto es una sucursal, un carrito o un almacén. Los cobros y el inventario cuelgan de aquí."
        />
      )}
      {locations && locations.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {locations.map((loc) => (
            <div key={loc.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ backgroundColor: loc.color || 'currentColor' }}
                />
                <p className="font-medium text-foreground">{loc.name}</p>
                {!loc.is_active && (
                  <span className="text-xs text-muted-foreground">(inactivo)</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {loc.kind === 'point_of_sale'
                  ? 'Punto de venta'
                  : loc.kind === 'warehouse'
                    ? 'Almacén'
                    : 'Oficina'}{' '}
                · {loc.slug}
              </p>
              <div className="mt-3">
                {/* No hay borrado: desactivar es el soft delete, para que los
                    cobros y movimientos que apuntan aquí nunca queden huérfanos. */}
                <LocationActiveToggle id={loc.id} isActive={loc.is_active} onDone={reload} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}
